/**
 * Helper compartido para los botones de "eliminar" (Fase 4.5 / auditoría final).
 *
 * POR QUÉ EXISTE
 * --------------
 * Desde la política de destrucción, `DELETE` ya no siempre borra:
 *   - Nivel Dios  -> elimina definitivamente
 *   - Resto       -> SUSPENDE (reversible)
 *   - Con dependencias vivas -> responde 409 con un mensaje útil
 *   - Forzar sin permiso     -> 403
 *
 * Los 5 botones de eliminar del CRM llamaban `await api.delete(...)` sin
 * `try/catch` y con un `toast.success('Eliminado')` fijo. Consecuencias
 * detectadas en la auditoría:
 *   1. Un 409 rompía la promesa: el usuario confirmaba y NO PASABA NADA,
 *      sin mensaje alguno. El mensaje útil del backend ("todavía lo están
 *      usando 12 leads") nunca se veía.
 *   2. El aviso decía "Eliminado" aunque solo se hubiera suspendido — falso
 *      para todos los perfiles menos el Owner, que son la mayoría.
 *
 * Este helper centraliza el manejo para que no vuelva a divergir entre
 * pantallas.
 */
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * Ejecuta el borrado/suspensión y avisa correctamente según lo que ocurrió.
 *
 * @param {string} url        ruta del DELETE (p. ej. `/leads/123`)
 * @param {object} opciones
 * @param {string} opciones.etiqueta   nombre legible ("Lead", "Propiedad"…)
 * @param {string} opciones.confirmar  texto del diálogo; si se omite, no confirma
 * @param {Function} opciones.alTerminar  callback para recargar la lista
 * @returns {Promise<boolean>} true si la operación se realizó
 */
export async function eliminarOSuspender(url, { etiqueta = 'Registro', confirmar, alTerminar } = {}) {
  if (confirmar && !window.confirm(confirmar)) return false;

  try {
    const r = await api.delete(url);
    const d = r?.data || {};

    if (d.suspended) {
      const arrastrados = (d.arrastrados || [])
        .map((a) => `${a.cantidad} ${a.tipo}`)
        .join(', ');
      toast.success(
        `${etiqueta} suspendido${arrastrados ? ` (y ${arrastrados})` : ''}`,
        { description: 'No se eliminó: sigue completo y puede reactivarse desde BackOffice → Suspensiones.' }
      );
    } else {
      toast.success(`${etiqueta} eliminado definitivamente`);
    }

    if (alTerminar) alTerminar();
    return true;
  } catch (e) {
    const detalle = e?.response?.data?.detail;
    const codigo = e?.response?.status;

    if (codigo === 409) {
      // El backend explica QUÉ lo está usando: es el mensaje más útil que hay.
      toast.error('No se puede eliminar', {
        description: typeof detalle === 'string' ? detalle : 'Otros registros dependen de este.',
        duration: 8000,
      });
    } else if (codigo === 403) {
      toast.error('No tienes permiso', {
        description: typeof detalle === 'string' ? detalle : 'Esta acción requiere un nivel superior.',
      });
    } else {
      toast.error(typeof detalle === 'string' ? detalle : `No se pudo eliminar ${etiqueta.toLowerCase()}`);
    }
    return false;
  }
}

export default eliminarOSuspender;
